import { useState } from 'react'

import { format, parseISO } from 'date-fns'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  DataTableFacetedFilter,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
} from '@fortem-monorepo/react-ui'
import { FundsSchemas, getErrorMessage } from '@fortem-monorepo/web-sdk'
import type { Funds } from '@fortem-monorepo/web-sdk'

import { useCreateFundHolidaysMutation } from '@/react-query/mutations/funds'

type Props = {
  funds: Funds.Fund[]
  initialFundIds: string[]
  onOpenChange: (open: boolean) => void
}

export const AddFundHolidaysDialog = ({ funds, initialFundIds, onOpenChange }: Props) => {
  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: {
        date: format(new Date(), 'yyyy-MM-dd'),
        comment: '',
        fund_ids: initialFundIds.map(Number),
      },
    },
    FundsSchemas.createFundHolidaysSchema,
  )
  const [confirmation, setConfirmation] = useState<Funds.CreateFundHolidaysBody | null>(null)
  const mutation = useCreateFundHolidaysMutation()
  const fundOptions = funds.map((fund) => ({ label: fund.fund_name, value: String(fund.fund_id) }))

  const review = form.handleSubmit((values) => {
    mutation.reset()
    setConfirmation(values)
  })

  return (
    <Dialog open onOpenChange={(open) => !mutation.isPending && onOpenChange(open)}>
      <DialogContent onEscapeKeyDown={(event) => mutation.isPending && event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{confirmation ? 'Confirm holidays' : 'Add holidays'}</DialogTitle>
          <DialogDescription>
            {confirmation ? 'Review the details before adding these holidays.' : 'Add a holiday for one or more funds.'}
          </DialogDescription>
        </DialogHeader>
        {confirmation ? (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-medium">Date</dt>
              <dd>{confirmation.date}</dd>
            </div>
            <div>
              <dt className="font-medium">Comment</dt>
              <dd className="whitespace-pre-wrap break-words">{confirmation.comment || 'No comment'}</dd>
            </div>
            <div>
              <dt className="font-medium">Funds ({confirmation.fund_ids.length})</dt>
              <dd>
                {funds
                  .filter((fund) => confirmation.fund_ids.includes(fund.fund_id))
                  .map((fund) => fund.fund_name)
                  .join(', ')}
              </dd>
            </div>
          </dl>
        ) : (
          <Form {...form}>
            <form id="add-fund-holidays" onSubmit={review} className="space-y-4">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <DatePicker
                      selected={field.value ? parseISO(field.value) : undefined}
                      onSelect={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                      size="sm"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="comment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="holiday-comment">Comment</FormLabel>
                    <Textarea id="holiday-comment" {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fund_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Funds</FormLabel>
                    <DataTableFacetedFilter
                      title="Funds"
                      options={fundOptions}
                      values={field.value.map(String)}
                      setValues={(values) => field.onChange(values.map(Number))}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        )}
        {mutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            {getErrorMessage(mutation.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" disabled={mutation.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {confirmation ? (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={mutation.isPending}
                onClick={() => {
                  mutation.reset()
                  setConfirmation(null)
                }}
              >
                Back
              </Button>
              <Button
                size="sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(confirmation, { onSuccess: () => onOpenChange(false) })}
              >
                {mutation.isPending ? 'Adding holidays...' : 'Confirm add holidays'}
              </Button>
            </>
          ) : (
            <Button size="sm" type="submit" form="add-fund-holidays" disabled={!form.formState.isValid}>
              Add holidays to fund(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
